import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useProjectFilter } from "@/context/ProjectFilterContext";
import { useAuth } from "@/context/AuthContext";
import { SectionCard, Loading, EmptyState, Money } from "@/components/common/ui";
import { KpiTile } from "@/components/KpiTile";
import { AnalyticsFilterBar } from "@/components/filters/AnalyticsFilterBar";
import { ChartTooltip } from "@/components/charts/ChartTooltip";
import { GovernedEditableField } from "@/components/GovernedEditableField";
import { PlannedBudgetDialog } from "@/components/dashboard/PlannedBudgetDialog";
import { SpendsSplitCharts } from "@/components/dashboard/SpendsSplitCharts";
import { CostEfficiency } from "@/components/dashboard/CostEfficiency";
import { SpendsExplorerUnified } from "@/components/dashboard/SpendsExplorerUnified";
import { CorporateSpends } from "@/components/dashboard/CorporateSpends";
import { CorporateOverview } from "@/components/dashboard/CorporateOverview";
import { DashboardSignals } from "@/components/dashboard/DashboardSignals";
import { ConsolidatedSpends } from "@/components/dashboard/ConsolidatedSpends";
import { ReferralPayouts } from "@/components/dashboard/ReferralPayouts";
import { KpiValueEditor } from "@/components/dashboard/KpiValueEditor";
import { CeoDigestSheet } from "@/components/dashboard/CeoDigestSheet";
import { BudgetAlertsStrip } from "@/components/dashboard/BudgetAlertsStrip";
import { EditableText } from "@/components/cms/EditableText";
import { C, axisProps } from "@/lib/chart";
import { formatCount, axisINR } from "@/lib/format";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
  Wallet, PiggyBank, Layers, Info, Coins, AlertTriangle, ShieldCheck,
} from "lucide-react";

const DASH = "\u2014";

const DEFAULT_SECTIONS = [
  { key: "mh-kpis", visible: true }, { key: "mh-verticals", visible: true },
  { key: "mh-explorer", visible: true },
  { key: "mh-signals", visible: true }, { key: "mh-referral", visible: true }, { key: "mh-flow", visible: true },
];

// Grouped narrative flow so anyone reading top-to-bottom understands the story:
// money health -> where the money goes -> funnel performance.
const SECTION_GROUPS = [
  { id: "health", title: "Budget & Spend Health",
    subtitle: "How much is allocated, planned and spent \u2014 and whether we're within budget for the selection.",
    keys: ["mh-kpis"] },
  { id: "spend", title: "Where the Money Goes",
    subtitle: "Spend by vertical, the unified investment + drill-down explorer, month-over-month trend, top vendors and referral payouts.",
    keys: ["mh-verticals", "mh-explorer", "mh-signals", "mh-referral"] },
  { id: "funnel", title: "Funnel & Performance",
    subtitle: "Lead \u2192 Site Visit \u2192 Booking efficiency and cost-per-outcome.",
    keys: ["mh-flow"] },
];

function GroupHeader({ index, title, subtitle, groupId }) {
  return (
    <div className="flex items-center gap-3" data-testid={`dash-group-header-${index}`}>
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--primary)/0.1)] font-heading text-sm font-semibold text-primary">{index}</span>
      <div className="min-w-0">
        <h2 className="font-heading text-base font-semibold leading-tight tracking-tight">
          <EditableText cmsKey={`dash.group.${groupId}.title`} fallback={title} />
        </h2>
        <p className="text-xs text-muted-foreground">
          <EditableText cmsKey={`dash.group.${groupId}.subtitle`} fallback={subtitle} multiline />
        </p>
      </div>
    </div>
  );
}

export default function ExecutiveOverview() {
  const { money } = useCurrency();
  const { isFull } = useAuth();
  const { period, selected, gran, month, half } = useProjectFilter();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [spendScope, setSpendScope] = useState("project"); // PART 3: project | corporate (strict either/or)
  const isCorpScope = spendScope === "corporate";
  const [corp, setCorp] = useState(null);
  const [corpLoading, setCorpLoading] = useState(false);

  useEffect(() => { api.dashboardConfig().then(setConfig).catch(() => setConfig(null)); }, []);

  const projKey = (selected || []).join(",");
  // Base marketing-head is ALWAYS project-only (corporate never blended in — PART 3 3.4).
  const reload = () => api.marketingHead(period, selected, false).then(setData).catch(() => {});

  useEffect(() => {
    let on = true; setKpiLoading(true);
    api.marketingHead(period, selected, false).then((d) => { if (on) { setData(d); setLoading(false); setKpiLoading(false); } })
      .catch(() => { if (on) { setLoading(false); setKpiLoading(false); } });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, projKey]);

  useEffect(() => {
    if (!isCorpScope) return;
    let on = true; setCorpLoading(true);
    api.corporateOverview(period, selected).then((d) => { if (on) { setCorp(d); setCorpLoading(false); } })
      .catch(() => { if (on) { setCorpLoading(false); } });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, projKey, spendScope]);

  if (loading && !data) return <div className="space-y-5"><Loading /></div>;
  if (!data) return <EmptyState title="No data" message="Could not load the marketing dashboard." />;

  const k = data.kpis;
  const activeMonths = data.active_months || [];
  const allProjects = data.all_projects || [];
  // Unified scope: with no explicit selection every section spans ALL projects so
  // KPIs, verticals, mix, explorer & signals reconcile to the same grand total.
  const scopeProjects = (selected && selected.length) ? selected : allProjects;

  // ---- Brand / Support (special-only) scope detection (S1) ----
  // The special "Brand" (Sumadhura) & "Support" (All Projects) entities plus any
  // discovered corporate/shared projects are org-level, not performance projects.
  // When the selection is EXCLUSIVELY these, performance-oriented sections
  // (funnel, project scorecard, referral payouts) are hidden to avoid empty/zero
  // metrics that only confuse the reader.
  const specialSet = new Set([...(data.corporate_projects || []), "Sumadhura", "All Projects"]);
  const hasSelection = (selected || []).length > 0;
  const isSpecialOnly = hasSelection && (selected || []).every((p) => specialSet.has(p));
  const hasSpecialSelected = (selected || []).some((p) => specialSet.has(p));
  // Top Vendors / signals correctness (S2): only blend in corporate/shared spend
  // when the scope is "all" or explicitly includes a special entity. When the CEO
  // has drilled into specific real projects, corporate spend must NOT bleed in.
  const includeCorporate = !hasSelection || hasSpecialSelected;
  const HIDE_FOR_SPECIAL = new Set(["mh-flow", "mh-referral"]);

  // ---- governed edit scope ----
  const singleProject = (selected || []).length === 1;
  const projectName = singleProject ? selected[0] : null;
  const inCurated = singleProject && (data.curated || []).includes(projectName);
  const canEditMonthly = isFull && inCurated && gran === "Monthly";
  const canEditAnnual = isFull && inCurated && (gran === "Monthly" || gran === "Quarterly");
  const saveValue = (field) => async (value, reason) => {
    await api.editDashboardValue({ project: projectName, month, field, value, reason });
    await reload();
  };
  const editHint = !isFull ? null
    : canEditMonthly ? { tone: "on", text: `Metric editing enabled \u00b7 ${projectName} \u00b7 ${month}` }
      : canEditAnnual ? { tone: "partial", text: `Targeted Revenue editable \u00b7 ${projectName}. Select a single month to edit spend / revenue / funnel values.` }
        : { tone: "off", text: "Select one project + one month (Month view) to edit dashboard metrics." };

  const editVal = (raw, display, field, canEdit) =>
    canEdit ? <GovernedEditableField value={raw} display={display} canEdit onSave={saveValue(field)}
      label={`${field.replace(/_/g, " ")} \u00b7 ${projectName} \u00b7 ${field === "targeted_revenue" ? "FY" : month}`}
      testPrefix={`dashboard-edit-${field}`} align="left" showLock={false} valueClassName="tabular-nums" /> : display(raw);

  const overPlan = (k.plan_vs_alloc || 0) > 1;

  // ---- inline KPI overrides (all four tiles; admin/leader only) ----
  const kpiScopeProjects = (selected && selected.length) ? selected.join(",") : undefined;
  const ovr = k.overridden || {};
  const saveKpi = (field) => async (value) => {
    await api.overrideDashboardKpi({ period, projects: kpiScopeProjects, field, value });
    await reload();
  };
  const resetKpi = (field) => async () => {
    await api.overrideDashboardKpi({ period, projects: kpiScopeProjects, field, value: null });
    await reload();
  };

  // ---- per-card visibility (dashboard builder) ----
  const cardCfg = config?.cards || {};
  const cardOn = (key) => cardCfg[key] !== false; // default visible

  // ---- dynamic budget-cross indicator ----
  const util = k.util || 0;
  const budgetInd = k.budget_allocated <= 0
    ? { tone: "neutral", label: "No budget allocated for this scope", pct: 0 }
    : k.total_spends > k.budget_allocated
      ? { tone: "danger", label: `Over budget by ${money(k.total_spends - k.budget_allocated)}`, pct: Math.round(util * 100) }
      : util >= 0.8
        ? { tone: "warning", label: `Nearing budget \u00b7 ${money(k.spare_budget)} spare`, pct: Math.round(util * 100) }
        : { tone: "success", label: `Within budget \u00b7 ${money(k.spare_budget)} spare`, pct: Math.round(util * 100) };
  const budgetToneCls = {
    danger: "border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger)/0.08)] text-[hsl(var(--danger))]",
    warning: "border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.09)] text-[hsl(var(--warning))]",
    success: "border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.08)] text-[hsl(var(--success))]",
    neutral: "bg-secondary text-muted-foreground",
  }[budgetInd.tone];
  const budgetBar = { danger: "hsl(var(--danger))", warning: "hsl(var(--warning))", success: "hsl(var(--success))", neutral: "hsl(var(--muted-foreground))" }[budgetInd.tone];

  const sections = {
    "mh-kpis": () => (
      isCorpScope ? (
        (corpLoading && !corp) ? (
          <div key="mh-kpis" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
        ) : (
          <div key="mh-kpis" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="mh-kpis">
            <KpiTile testId="kpi-budget-allocated" title="Budget Allocated" accent="hsl(var(--muted-foreground))" icon={PiggyBank}
              value={<span className="text-muted-foreground" data-testid="mh-kpi-budget-allocated-value">{DASH}</span>} sub="Not tracked for corporate spends" />
            <KpiTile testId="kpi-planned-budget" title="Planned Budget" accent="hsl(var(--muted-foreground))" icon={Layers}
              value={<span className="text-muted-foreground" data-testid="mh-kpi-planned-budget-value">{DASH}</span>} sub="Not tracked for corporate spends" />
            <KpiTile testId="kpi-total-spends" title="Total Spends" accent="hsl(var(--warning))" icon={Wallet}
              value={<span data-testid="mh-kpi-total-spends-value"><Money value={corp?.total || 0} /></span>}
              sub={`${formatCount(corp?.count || 0)} line items`} />
            <KpiTile testId="kpi-spare-budget" title="Spare Budget" accent="hsl(var(--muted-foreground))" icon={Coins}
              value={<span className="text-muted-foreground" data-testid="mh-kpi-spare-budget-value">{DASH}</span>} sub="Not tracked for corporate spends" />
          </div>
        )
      ) : (
      kpiLoading ? (
        <div key="mh-kpis" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      ) : (
        <div key="mh-kpis" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="mh-kpis">
          {cardOn("kpi-budget-allocated") && (
          <KpiTile testId="kpi-budget-allocated" title="Budget Allocated" accent="hsl(var(--primary))" icon={PiggyBank}
            value={<KpiValueEditor testId="kpi-edit-budget-allocated" value={k.budget_allocated} overridden={!!ovr.budget_allocated} canEdit={isFull} onSave={saveKpi("budget_allocated")} onReset={resetKpi("budget_allocated")}
              display={<span data-testid="mh-kpi-budget-allocated-value"><Money value={k.budget_allocated} /></span>} />}
            sub="Annual cap set by management" />
          )}
          {cardOn("kpi-planned-budget") && (
          <KpiTile testId="kpi-planned-budget" title="Planned Budget" accent="hsl(var(--info))" icon={Layers}
            value={<KpiValueEditor testId="kpi-edit-planned-budget" value={k.planned_budget} overridden={!!ovr.planned_budget} canEdit={isFull} onSave={saveKpi("planned_budget")} onReset={resetKpi("planned_budget")}
              display={<span data-testid="mh-kpi-planned-budget-value"><Money value={k.planned_budget} /></span>} />}>
            <Badge variant="outline" className="mt-1 border-[hsl(var(--info)/0.4)] text-[10px] text-[hsl(var(--info))]" data-testid="mh-plan-vs-alloc-badge">
              {ovr.planned_budget ? "Manually set" : "80% of allocated budget"}
            </Badge>
          </KpiTile>
          )}
          {cardOn("kpi-total-spends") && (
          <KpiTile testId="kpi-total-spends" title="Total Spends" accent="hsl(var(--warning))" icon={Wallet}
            value={<KpiValueEditor testId="kpi-edit-total-spends" value={k.total_spends} overridden={!!ovr.total_spends} canEdit={isFull} onSave={saveKpi("total_spends")} onReset={resetKpi("total_spends")}
              display={<span data-testid="mh-kpi-total-spends-value"><Money value={k.total_spends} /></span>} />}
            sub={`${formatCount(k.invoices)} line items`} />
          )}
          {cardOn("kpi-spare-budget") && (
          <KpiTile testId="kpi-spare-budget" title="Spare Budget" accent={k.spare_budget < 0 ? "hsl(var(--danger))" : "hsl(var(--success))"} icon={Coins}
            className={k.spare_budget < 0 ? "border-[hsl(var(--danger)/0.35)] bg-[hsl(var(--danger)/0.06)]" : ""}
            value={<KpiValueEditor testId="kpi-edit-spare-budget" value={k.spare_budget} overridden={!!ovr.spare_budget} canEdit={isFull} onSave={saveKpi("spare_budget")} onReset={resetKpi("spare_budget")}
              display={<span data-testid="mh-kpi-spare-budget-value"><Money value={k.spare_budget} colorNegative /></span>} />}>
            <div className="space-y-1.5">
              <div className="flex justify-between text-[11px] text-muted-foreground"><span>Utilised of allocation</span><span className="tabular-nums">{Math.round((k.util || 0) * 100)}%</span></div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-[hsl(var(--surface-3))]"><div className="h-full rounded-full" style={{ width: `${Math.min(100, (k.util || 0) * 100)}%`, background: (k.util || 0) >= 1 ? "hsl(var(--danger))" : (k.util || 0) >= 0.8 ? "hsl(var(--warning))" : "hsl(var(--success))" }} /></div>
            </div>
          </KpiTile>
          )}
        </div>
      )
      )
    ),

    "mh-verticals": () => (
      isCorpScope ? (
        <CorporateSpends key="mh-verticals" period={period} overview={corp} loading={corpLoading} />
      ) : (
      <SectionCard key="mh-verticals" title={<EditableText cmsKey="dash.verticals.title" fallback="Spends by Vertical" />}
        description={<EditableText cmsKey="dash.verticals.description" fallback="Planned budget vs actual spend. Spend within the plan is green; overspend beyond the plan is red. Verticals without a set budget appear as pure spend." multiline />}
        action={
          <div className="flex flex-wrap items-center gap-2">
            {isFull ? <PlannedBudgetDialog onSaved={reload} /> : null}
          </div>
        }
        testId="card-mh-verticals">
        <div className="px-2 py-4" style={{ height: 340 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.verticals} margin={{ top: 8, right: 12, left: 4, bottom: 0 }} barCategoryGap="22%">
              <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
              <XAxis dataKey="vertical" {...axisProps} interval={0} angle={-20} textAnchor="end" height={54} />
              <YAxis tickFormatter={axisINR} {...axisProps} width={48} />
              <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="planned" name="Planned budget" fill="hsl(var(--info))" radius={[4, 4, 0, 0]} barSize={16} isAnimationActive={false} />
              <Bar dataKey="spent_within" stackId="spent" name="Spent · within plan" fill="hsl(var(--success))" barSize={16} isAnimationActive={false} radius={[0, 0, 0, 0]} />
              <Bar dataKey="spent_over" stackId="spent" name="Spent · over plan" fill="hsl(var(--danger))" barSize={16} isAnimationActive={false} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {!singleProject && <SpendsSplitCharts period={period} projects={selected} />}
      </SectionCard>
      )
    ),

    "mh-explorer": () => (
      <SpendsExplorerUnified key="mh-explorer" period={period} projects={scopeProjects} />
    ),

    "mh-signals": () => (
      <DashboardSignals key="mh-signals" period={period} projects={scopeProjects} includeCorporate={includeCorporate} />
    ),

    "mh-referral": () => (
      <ReferralPayouts key="mh-referral" period={period} projects={scopeProjects} />
    ),

    "mh-flow": () => (
      <CostEfficiency key="mh-flow" data={data} isFull={isFull} onSaved={reload} />
    ),
  };

  const savedSections = (config?.sections && config.sections.length) ? config.sections : DEFAULT_SECTIONS;
  const visOf = Object.fromEntries(savedSections.map((s) => [s.key, s.visible !== false]));
  const sectionVisible = (key) => {
    if (isSpecialOnly && HIDE_FOR_SPECIAL.has(key)) return false;
    // Under Corporate Spends the view is focused on corporate KPIs + the corporate
    // distribution chart; project-centric analytics are hidden (PART 3 3.4/3.5).
    if (isCorpScope && key !== "mh-kpis" && key !== "mh-verticals") return false;
    return !!sections[key] && (visOf[key] ?? true);
  };

  // budget health strip (alerts + within/over budget indicator) — always in group 1
  const healthStrip = (
    <>
      <BudgetAlertsStrip />
      <div className={"flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border px-4 py-2.5 " + budgetToneCls} data-testid="budget-cross-indicator">
        <span className="flex items-center gap-2 text-sm font-semibold">
          {budgetInd.tone === "danger" ? <AlertTriangle className="h-4 w-4" /> : budgetInd.tone === "warning" ? <AlertTriangle className="h-4 w-4" /> : budgetInd.tone === "success" ? <ShieldCheck className="h-4 w-4" /> : <Info className="h-4 w-4" />}
          {budgetInd.label}
        </span>
        <span className="text-xs opacity-90" data-testid="budget-cross-detail">Spent {money(k.total_spends)} of {money(k.budget_allocated)} allocated</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs font-semibold tabular-nums">{budgetInd.pct}%</span>
          <div className="h-2 w-32 overflow-hidden rounded-full bg-[hsl(var(--surface-3))]">
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, budgetInd.pct)}%`, background: budgetBar }} />
          </div>
        </div>
      </div>
    </>
  );

  // build the visible groups (a group with no visible members is dropped, except
  // "health" which always shows the budget strip) and number them dynamically.
  const visibleGroups = SECTION_GROUPS
    .map((g) => ({ ...g, members: g.keys.filter(sectionVisible) }))
    .filter((g) => g.members.length > 0 || g.id === "health");

  // spend-type toggle (Project vs Corporate) — styled identically to the period tabs
  const spendToggle = (
    <ToggleGroup type="single" value={spendScope} onValueChange={(v) => v && setSpendScope(v)}
      className="rounded-lg border bg-card p-0.5" data-testid="health-scope-toggle">
      <ToggleGroupItem value="project" className="h-7 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid="health-scope-project">Project Spends</ToggleGroupItem>
      <ToggleGroupItem value="corporate" className="h-7 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid="health-scope-corporate">Corporate Spends</ToggleGroupItem>
    </ToggleGroup>
  );

  return (
    <div className="space-y-6" data-testid="executive-overview">
      <div className="flex flex-wrap items-center justify-end gap-2" data-testid="dashboard-toolbar">
        <CeoDigestSheet />
      </div>

      <ConsolidatedSpends />

      {isCorpScope ? (
        <section className="space-y-4" data-testid="dash-group-corporate">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <GroupHeader index={1} title="Corporate Spends"
              subtitle="Company-level spend split across Brand, Signet & Tech — never blended with project spends."
              groupId="corporate" />
            <div className="flex flex-wrap items-center gap-2 lg:justify-end" data-testid="health-controls">
              <AnalyticsFilterBar embedded activeMonths={activeMonths} filtersConfig={config?.filters} allowedProjects={allProjects} corporateProjects={data.corporate_projects || []} multiMonth />
              {spendToggle}
            </div>
          </div>
          <CorporateOverview period={period} overview={corp} loading={corpLoading} />
        </section>
      ) : (
        visibleGroups.map((g, i) => (
          <section key={g.id} className="space-y-4" data-testid={`dash-group-${g.id}`}>
            {g.id === "health" ? (
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <GroupHeader index={i + 1} title={g.title} subtitle={g.subtitle} groupId={g.id} />
                <div className="flex flex-wrap items-center gap-2 lg:justify-end" data-testid="health-controls">
                  <AnalyticsFilterBar embedded activeMonths={activeMonths} filtersConfig={config?.filters} allowedProjects={allProjects} corporateProjects={data.corporate_projects || []} multiMonth />
                  {spendToggle}
                </div>
              </div>
            ) : (
              <GroupHeader index={i + 1} title={g.title} subtitle={g.subtitle} groupId={g.id} />
            )}
            {g.id === "health" && !isCorpScope && healthStrip}
            {g.members.map((key) => (
              <React.Fragment key={key}>
                {sections[key]()}
              </React.Fragment>
            ))}
          </section>
        ))
      )}
    </div>
  );
}
